import {
  AfterViewChecked,
  Attribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { Card } from '@upmc-healthplan-npm/ods-wc';

export interface IOverflowMenuConfig {
  text?: string;
  callback: ($event: any, columnData: any) => void;
  disabled?: (columnData: any) => boolean;
  ariaLabel: (columnData: any) => string;
}

@Component({
  selector: 'app-overflow-menu',
  templateUrl: './overflow-menu.component.html',
  styleUrls: ['./overflow-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverflowMenuComponent implements OnInit, AfterViewChecked, OnDestroy {
  element = inject(ElementRef);
  cdr = inject(ChangeDetectorRef);

  private bodyCard: Card;
  protected id = Math.floor(Math.random() * 100000000);
  iconSrc: string;
  expanded = false;

  @Input() config: IOverflowMenuConfig[];
  @Input() metadata: any;
  @Input() ariaLabel: string;

  @HostListener('document:click', ['$event'])
  click(event: PointerEvent): void {
    if(!this.element.nativeElement.contains(event.target)) {
      this.removeOpenCards();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  escPressed(event: KeyboardEvent): void {
    this.removeOpenCards();
  }

  @ViewChild('image') image: ElementRef<HTMLImageElement>;
  @ViewChild('overflowOptionsMenu') overflowOptionsMenu: ElementRef<Card>;

  constructor(
    @Attribute('icon') public icon: string
  ) {}

  ngOnInit(): void {
    this.iconSrc = this.icon;
  }

  ngOnDestroy(): void {
    this.bodyCard?.remove();
  }

  ngAfterViewChecked(): void {
    // bind the events here instead of in the template because if we do it
    // in the template then change detection destroys the menu as soon as it's created
    this.image.nativeElement.onmouseenter = this.startHover.bind(this);
    this.image.nativeElement.onfocus = this.startHover.bind(this);
    this.image.nativeElement.onkeydown = this.focusNext.bind(this);
  }

  private startHover(): void {
    this.removeOpenCards();
    this.expanded = true;
    
    // add a copy of the ods-card to the body
    this.overflowOptionsMenu.nativeElement.classList.add('show', 'detached');
    this.bodyCard = document.body.appendChild(this.overflowOptionsMenu.nativeElement);
    this.bodyCard.onkeydown = this.focusNext.bind(this);

    // position the copy correctly
    const rect = this.image.nativeElement.getBoundingClientRect();
    const top = rect.top + window.scrollY + 20;
    const left = rect.left + window.scrollX - this.bodyCard.clientWidth + 30;
    this.bodyCard.style.top = `${top}px`;
    this.bodyCard.style.left = `${left}px`;
    this.cdr.detectChanges();
  }

  private focusNext(e: KeyboardEvent) {
    // listen for tab keypress
    if(e.code.toLowerCase() === 'tab') {
      if(e.shiftKey) {
        // if shift + tab
        const focusedElement = document.activeElement;
        const firstAction = this.bodyCard?.querySelector('li:first-of-type');

        if(focusedElement === this.image.nativeElement) {
          // if we're focused on the image then we need to close any open menus
          this.removeOpenCards();
        } else if(focusedElement === firstAction) {
          // if we're on the first item in the menu then we need to set
          // focus to the image
          e.preventDefault();
          this.image.nativeElement.focus();
          this.removeOpenCards();
        }
      } else {
        if(!this.bodyCard) {
          return;
        }

        const focusedElement = document.activeElement;
        const lastAction = this.bodyCard?.querySelector('li:last-of-type');

        // if we're focused on the image then we need to set focus to the
        // first item in the menu
        if(focusedElement === this.image.nativeElement) {
          e.preventDefault();
          const firstAction: HTMLLIElement = this.bodyCard.querySelector('li:first-of-type');
          firstAction.focus();
          this.expanded = true;
        } else if(focusedElement === lastAction) {
          // if we're focused on the last item in the menu then we need
          // to set focus back to the image
          e.preventDefault();
          this.image.nativeElement.focus();
          this.removeOpenCards();
        }
      }
    } else if(['enter', 'space'].includes(e.code.toLowerCase())) {
      // enter or spacebar pressed
      if(this.expanded) {
        this.removeOpenCards();
      } else {
        this.startHover();
      }
    }
  }

  private removeOpenCards(): void {
    document.querySelectorAll('ods-card.detached').forEach(i => i.remove());
    this.bodyCard = null;
    this.expanded = false;
    this.cdr.detectChanges();
  }
}
